import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { tenantAPI } from '../../services/api';
import { tenantUploadAPI } from '../../services/uploadAPI';
import LogoUpload from '../../components/LogoUpload/LogoUpload';
import AdminAddressPanel from './AdminAddressPanel';
import './TenantProfile.css';

/**
 * Tenant Profile Page
 * Company branding and settings management
 */
const TenantProfile = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [tenant, setTenant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('info');

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        type: 'enterprise',
        contactPerson: {
            name: '',
            email: '',
            mobile: ''
        },
        address: {
            street: '',
            city: '',
            state: '',
            country: '',
            pincode: ''
        },
        branding: {
            primaryColor: '#3b82f6',
            secondaryColor: '#1e3a8a',
            theme: 'light'
        },
        settings: {
            timezone: 'UTC',
            language: 'en'
        }
    });

    useEffect(() => {
        fetchTenant();
    }, [id]);

    const fetchTenant = async () => {
        try {
            setLoading(true);
            const response = await tenantAPI.getById(id);
            const tenantData = response.data.data?.tenant || response.data.data;
            setTenant(tenantData);
            setFormData(tenantData);
        } catch (error) {
            toast.error('Failed to load tenant profile');
            navigate('/tenants');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const keys = name.split('.');

        if (keys.length === 1) {
            setFormData({ ...formData, [name]: value });
        } else if (keys.length === 2) {
            setFormData({
                ...formData,
                [keys[0]]: {
                    ...formData[keys[0]],
                    [keys[1]]: value
                }
            });
        }
    };

    const handleLogoUpload = async (file) => {
        await tenantUploadAPI.uploadLogo(id, file);
        await fetchTenant(); // Refresh data
    };

    const handleFaviconUpload = async (file) => {
        await tenantUploadAPI.uploadFavicon(id, file);
        await fetchTenant();
    };

    const handleBackgroundUpload = async (file) => {
        await tenantUploadAPI.uploadBackground(id, file);
        await fetchTenant();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            await tenantAPI.update(id, formData);
            toast.success('Tenant profile updated successfully');
            await fetchTenant();
        } catch (error) {
            toast.error('Failed to update tenant profile');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="page-container-modern">
                <div className="loading-modern">
                    <div className="spinner-modern"></div>
                    <p>Loading tenant profile...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container-modern">
            {/* Header */}
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <button
                            className="btn-secondary-modern btn-sm mb-2"
                            onClick={() => navigate('/tenants')}
                        >
                            <i className="fas fa-arrow-left"></i>
                            Back to Tenants
                        </button>
                        <h1 className="page-title-modern">{tenant?.name}</h1>
                        <p className="page-subtitle-modern">Manage company profile and branding</p>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="tabs">
                <button
                    className={`tab ${activeTab === 'info' ? 'active' : ''}`}
                    onClick={() => setActiveTab('info')}
                >
                    <i className="fas fa-info-circle mr-2"></i>
                    Company Info
                </button>
                <button
                    className={`tab ${activeTab === 'branding' ? 'active' : ''}`}
                    onClick={() => setActiveTab('branding')}
                >
                    <i className="fas fa-palette mr-2"></i>
                    Branding
                </button>
                <button
                    className={`tab ${activeTab === 'address' ? 'active' : ''}`}
                    onClick={() => setActiveTab('address')}
                >
                    <i className="fas fa-globe mr-2"></i>
                    Admin Address
                </button>
                <button
                    className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
                    onClick={() => setActiveTab('settings')}
                >
                    <i className="fas fa-cog mr-2"></i>
                    Settings
                </button>
            </div>

            {/* Content */}
            <form onSubmit={handleSubmit}>
                {/* Company Info Tab */}
                {activeTab === 'info' && (
                    <div className="resource-card-modern">
                        <div className="card-body">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Company Name *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Type</label>
                                    <select
                                        name="type"
                                        value={formData.type}
                                        onChange={handleInputChange}
                                    >
                                        <option value="hotel">Hotel</option>
                                        <option value="isp">ISP</option>
                                        <option value="mall">Shopping Mall</option>
                                        <option value="campus">Campus</option>
                                        <option value="enterprise">Enterprise</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>

                                <div className="form-group span-2">
                                    <label>Description</label>
                                    <textarea
                                        name="description"
                                        value={formData.description || ''}
                                        onChange={handleInputChange}
                                        rows="3"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Contact Person</label>
                                    <input
                                        type="text"
                                        name="contactPerson.name"
                                        value={formData.contactPerson?.name || ''}
                                        onChange={handleInputChange}
                                        placeholder="Full Name"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Contact Email</label>
                                    <input
                                        type="email"
                                        name="contactPerson.email"
                                        value={formData.contactPerson?.email || ''}
                                        onChange={handleInputChange}
                                        placeholder="email@example.com"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Contact Mobile</label>
                                    <input
                                        type="tel"
                                        name="contactPerson.mobile"
                                        value={formData.contactPerson?.mobile || ''}
                                        onChange={handleInputChange}
                                        placeholder="+1234567890"
                                    />
                                </div>
                            </div>

                            <button type="submit" className="btn-primary-modern mt-4" disabled={saving}>
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Branding Tab */}
                {activeTab === 'branding' && (
                    <div className="branding-grid">
                        <div className="resource-card-modern">
                            <div className="card-header">
                                <h3>Company Logo</h3>
                                <p className="text-sm text-muted">
                                    Your main company logo (recommended: 512x512px)
                                </p>
                            </div>
                            <div className="card-body">
                                <LogoUpload
                                    currentLogo={tenant?.branding?.logo?.url}
                                    onUpload={handleLogoUpload}
                                    title="Company Logo"
                                    aspectRatio="square"
                                />
                            </div>
                        </div>

                        <div className="resource-card-modern">
                            <div className="card-header">
                                <h3>Favicon</h3>
                                <p className="text-sm text-muted">
                                    Browser tab icon (recommended: 32x32px)
                                </p>
                            </div>
                            <div className="card-body">
                                <LogoUpload
                                    currentLogo={tenant?.branding?.favicon?.url}
                                    onUpload={handleFaviconUpload}
                                    title="Favicon"
                                    aspectRatio="square"
                                    maxSizeMB={1}
                                />
                            </div>
                        </div>

                        <div className="resource-card-modern span-2">
                            <div className="card-header">
                                <h3>Background Image</h3>
                                <p className="text-sm text-muted">
                                    Portal background (recommended: 1920x1080px)
                                </p>
                            </div>
                            <div className="card-body">
                                <LogoUpload
                                    currentLogo={tenant?.branding?.backgroundImage?.url}
                                    onUpload={handleBackgroundUpload}
                                    title="Background Image"
                                    aspectRatio="wide"
                                />
                            </div>
                        </div>

                        <div className="resource-card-modern span-2">
                            <div className="card-header">
                                <h3>Color Scheme</h3>
                            </div>
                            <div className="card-body">
                                <div className="color-picker-grid">
                                    <div className="form-group">
                                        <label>Primary Color</label>
                                        <div className="color-input-group">
                                            <input
                                                type="color"
                                                name="branding.primaryColor"
                                                value={formData.branding?.primaryColor || '#3b82f6'}
                                                onChange={handleInputChange}
                                                className="color-picker"
                                            />
                                            <input
                                                type="text"
                                                value={formData.branding?.primaryColor || '#3b82f6'}
                                                onChange={handleInputChange}
                                                name="branding.primaryColor"
                                                className="color-hex"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Secondary Color</label>
                                        <div className="color-input-group">
                                            <input
                                                type="color"
                                                name="branding.secondaryColor"
                                                value={formData.branding?.secondaryColor || '#1e3a8a'}
                                                onChange={handleInputChange}
                                                className="color-picker"
                                            />
                                            <input
                                                type="text"
                                                value={formData.branding?.secondaryColor || '#1e3a8a'}
                                                onChange={handleInputChange}
                                                name="branding.secondaryColor"
                                                className="color-hex"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Theme</label>
                                        <select
                                            name="branding.theme"
                                            value={formData.branding?.theme || 'light'}
                                            onChange={handleInputChange}
                                        >
                                            <option value="light">Light</option>
                                            <option value="dark">Dark</option>
                                            <option value="auto">Auto</option>
                                        </select>
                                    </div>
                                </div>

                                <button type="submit" className="btn-primary-modern mt-4" disabled={saving}>
                                    {saving ? 'Saving...' : 'Save Colors'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                    <div className="resource-card-modern">
                        <div className="card-body">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Timezone</label>
                                    <select
                                        name="settings.timezone"
                                        value={formData.settings?.timezone || 'UTC'}
                                        onChange={handleInputChange}
                                    >
                                        <option value="UTC">UTC</option>
                                        <option value="America/New_York">Eastern Time</option>
                                        <option value="America/Chicago">Central Time</option>
                                        <option value="America/Los_Angeles">Pacific Time</option>
                                        <option value="Europe/London">London</option>
                                        <option value="Asia/Kolkata">India</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Language</label>
                                    <select
                                        name="settings.language"
                                        value={formData.settings?.language || 'en'}
                                        onChange={handleInputChange}
                                    >
                                        <option value="en">English</option>
                                        <option value="es">Spanish</option>
                                        <option value="fr">French</option>
                                        <option value="de">German</option>
                                        <option value="hi">Hindi</option>
                                    </select>
                                </div>
                            </div>

                            <button type="submit" className="btn-primary-modern mt-4" disabled={saving}>
                                {saving ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>
                )}
            </form>

            {/* Admin Address — outside the profile form: it saves on its own */}
            {activeTab === 'address' && <AdminAddressPanel tenantId={id} />}
        </div>
    );
};

export default TenantProfile;
